"use client";
import React, { useState } from "react";
import axios from "axios";
// ✅ Import docx libraries
import {
  Document,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  WidthType,
  TextRun, // ✅ We use this for bold text
  HeadingLevel,
} from "docx";
import { saveAs } from "file-saver";

import LoadingIndicator from "../../components/common/loadingIndicator/LoadingIndicator";
import PrimaryButton from "../../components/common/buttons/PrimaryButton";
import {
  Search,
  MapPin,
  Truck,
  Car,
  Calendar,
  XCircle,
  Users,
  Download,
  Eye,
  FileText,
} from "lucide-react";

// Updated Interface
interface PassengerInfo {
  name: string;
  phone: string;
  pnr: string;
  bookingStatus: string;
}

interface FlightArrival {
  flightId: number;
  flightNumber: string;
  arrivalTime: string;
  origin: string;
  destination: string;
  totalPassengers: number;
  passengers: PassengerInfo[];
  recommendedVehicle: "Car" | "Van" | "None";
  estimatedVehiclesNeeded: number;
  airline: string;
}

const TransportScheduler = () => {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [routeFrom, setRouteFrom] = useState("");
  const [routeTo, setRouteTo] = useState("");

  const [arrivals, setArrivals] = useState<FlightArrival[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [filterFlightNo, setFilterFlightNo] = useState("");

  const [selectedFlightForModal, setSelectedFlightForModal] =
    useState<FlightArrival | null>(null);

  // --- Handlers ---
  const fetchSchedule = async () => {
    setLoading(true);
    setHasSearched(true);
    try {
      const API_URL =
        process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";
      const params: any = {};
      if (selectedDate) params.date = selectedDate;
      if (routeFrom) params.from = routeFrom;
      if (routeTo) params.to = routeTo;

      const res = await axios.get(`${API_URL}/admin/transport/schedule`, {
        params,
      });
      setArrivals(res.data);
    } catch (error) {
      console.error("Failed to fetch schedule", error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedDate("");
    setRouteFrom("");
    setRouteTo("");
    setArrivals([]);
    setHasSearched(false);
  };

  // --- ✅ WORD Generator (FIXED) ---
  const generateWord = () => {
    const tableRows = [];

    // 1. Header Row
    // We use TextRun to apply 'bold: true'
    tableRows.push(
      new TableRow({
        children: [
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: "Flight", bold: true })],
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: "Passenger Name", bold: true })],
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: "Contact", bold: true })],
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: "PNR", bold: true })],
              }),
            ],
          }),
          new TableCell({
            children: [
              new Paragraph({
                children: [new TextRun({ text: "Status", bold: true })],
              }),
            ],
          }),
        ],
        tableHeader: true,
      })
    );

    // 2. Data Rows
    arrivals.forEach((flight) => {
      if (flight.passengers.length > 0) {
        flight.passengers.forEach((p) => {
          tableRows.push(
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      text: `${flight.flightNumber} (${flight.origin}-${flight.destination})`,
                    }),
                  ],
                }),
                new TableCell({
                  children: [new Paragraph(p.name)],
                }),
                new TableCell({
                  children: [new Paragraph(p.phone)],
                }),
                new TableCell({
                  children: [new Paragraph(p.pnr)],
                }),
                new TableCell({
                  children: [new Paragraph(p.bookingStatus)],
                }),
              ],
            })
          );
        });
      }
    });

    const doc = new Document({
      sections: [
        {
          properties: {},
          children: [
            new Paragraph({
              text: `Transport Manifest - ${selectedDate || "All Dates"}`,
              heading: HeadingLevel.HEADING_1,
              spacing: { after: 200 },
            }),
            new Paragraph({
              text: `Route: ${routeFrom || "Any"} to ${routeTo || "Any"}`,
              spacing: { after: 400 },
            }),
            new Table({
              rows: tableRows,
              width: { size: 100, type: WidthType.PERCENTAGE },
            }),
          ],
        },
      ],
    });

    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, `Transport_Manifest_${selectedDate}.docx`);
    });
  };

  const filteredArrivals = arrivals.filter((item) => {
    return item.flightNumber
      .toLowerCase()
      .includes(filterFlightNo.toLowerCase());
  });

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Truck className="text-blue-600" /> Transport Scheduler
        </h1>
        {hasSearched && arrivals.length > 0 && (
          <button
            onClick={generateWord}
            className="flex items-center gap-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition shadow-sm"
          >
            <FileText size={18} /> Download Word
          </button>
        )}
      </div>

      {/* --- CONTROLS BAR --- */}
      <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1">
              <Calendar size={14} /> Date
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full border-gray-300 rounded-lg p-2.5 border focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1">
              <MapPin size={14} /> Origin (From)
            </label>
            <input
              type="text"
              value={routeFrom}
              onChange={(e) => setRouteFrom(e.target.value.toUpperCase())}
              placeholder="Any Origin"
              className="w-full border-gray-300 rounded-lg p-2.5 border uppercase focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1">
              <MapPin size={14} /> Destination (To)
            </label>
            <input
              type="text"
              value={routeTo}
              onChange={(e) => setRouteTo(e.target.value.toUpperCase())}
              placeholder="Any Destination"
              className="w-full border-gray-300 rounded-lg p-2.5 border uppercase focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <div className="flex gap-2">
            <PrimaryButton onClick={fetchSchedule} type="button">
              {loading ? "Searching..." : "Find Schedules"}
            </PrimaryButton>
            {hasSearched && (
              <button
                onClick={clearFilters}
                className="p-2.5 text-gray-500 hover:text-red-600 border rounded-lg hover:bg-red-50"
              >
                <XCircle size={20} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* --- RESULTS TABLE --- */}
      {loading ? (
        <div className="p-20 flex justify-center">
          <LoadingIndicator />
        </div>
      ) : (
        <>
          {hasSearched && (
            <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden animate-fadeIn">
              <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                <h3 className="font-bold text-gray-700">
                  Arrivals:{" "}
                  <span className="text-blue-600">
                    {filteredArrivals.length} Flights
                  </span>
                </h3>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Filter Flight No..."
                    value={filterFlightNo}
                    onChange={(e) => setFilterFlightNo(e.target.value)}
                    className="pl-10 w-full border border-gray-300 rounded-lg p-2 text-sm focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 text-gray-500 font-semibold text-xs uppercase">
                  <tr>
                    <th className="px-6 py-3 text-left tracking-wider">
                      Flight Info
                    </th>
                    <th className="px-6 py-3 text-left tracking-wider">
                      Date/Time
                    </th>
                    <th className="px-6 py-3 text-center tracking-wider">
                      Passengers
                    </th>
                    <th className="px-6 py-3 text-center tracking-wider">
                      Vehicle
                    </th>
                    <th className="px-6 py-3 text-right tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200 text-sm">
                  {filteredArrivals.map((item) => (
                    <tr
                      key={item.flightId}
                      className="hover:bg-blue-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="font-bold text-gray-900 text-lg">
                            {item.flightNumber}
                          </span>
                          <span className="text-gray-500 text-xs">
                            {item.airline}
                          </span>
                          <span className="text-gray-400 text-xs mt-1">
                            {item.origin} → {item.destination}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700 font-medium">
                        {item.arrivalTime.replace("T", " ")}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {item.totalPassengers > 0 ? (
                          <button
                            onClick={() => setSelectedFlightForModal(item)}
                            className="flex items-center justify-center gap-1 mx-auto bg-blue-100 text-blue-700 hover:bg-blue-200 px-3 py-1 rounded-full text-xs font-bold transition"
                          >
                            <Users size={14} /> {item.totalPassengers} Pax{" "}
                            <Eye size={12} className="ml-1 opacity-50" />
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs">0 Pax</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex justify-center">
                          {item.totalPassengers > 0 ? (
                            <div
                              className={`flex items-center gap-2 px-4 py-1.5 rounded-lg border ${
                                item.recommendedVehicle === "Van"
                                  ? "bg-orange-50 border-orange-200 text-orange-700"
                                  : "bg-green-50 border-green-200 text-green-700"
                              }`}
                            >
                              {item.recommendedVehicle === "Van" ? (
                                <Truck size={16} />
                              ) : (
                                <Car size={16} />
                              )}
                              <span className="font-bold">
                                {item.recommendedVehicle}
                              </span>
                              <span className="text-xs bg-white/50 px-1.5 rounded">
                                x{item.estimatedVehiclesNeeded}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          disabled={item.totalPassengers === 0}
                          className={`text-xs font-medium px-3 py-1.5 rounded ${
                            item.totalPassengers > 0
                              ? "bg-black text-white"
                              : "bg-gray-200 text-gray-400"
                          }`}
                        >
                          Manage Pickup
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* --- PASSENGER MODAL --- */}
      {selectedFlightForModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden animate-fadeIn">
            <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
              <h3 className="font-bold text-lg text-gray-800">
                Passengers for {selectedFlightForModal.flightNumber}
              </h3>
              <button
                onClick={() => setSelectedFlightForModal(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XCircle size={24} />
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {selectedFlightForModal.passengers.length > 0 ? (
                <div className="space-y-3">
                  {selectedFlightForModal.passengers.map((p, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                    >
                      <div>
                        <p className="font-semibold text-gray-800">{p.name}</p>
                        <p className="text-xs text-gray-500 font-mono mt-1">
                          PNR:{" "}
                          <span className="bg-yellow-100 px-1 rounded text-yellow-800">
                            {p.pnr}
                          </span>
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-blue-600">
                          {p.phone}
                        </p>
                        <span
                          className={`text-[10px] px-2 py-0.5 rounded-full ${
                            p.bookingStatus === "Paid"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {p.bookingStatus}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-4">
                  No passenger details found.
                </p>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 text-right">
              <button
                onClick={() => setSelectedFlightForModal(null)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportScheduler;
